import { createFetchConfig, getApiUrl } from '../../config/api'

export interface CustomField {
  id: string
  name: string
  key: string
  type: 'TEXT' | 'NUMBER' | 'DATE' | 'SELECT'
  options?: string[] | null
  required: boolean
  isGlobal: boolean
  createdAt: string
  updatedAt: string
  creator: {
    id: string
    name: string
    email: string
  }
  _count?: {
    projects: number
    values: number
  }
}

export interface ProjectCustomField {
  id: string
  projectId: string
  fieldId: string
  enabled: boolean
  required: boolean
  showInCard: boolean
  order: number
  field: CustomField
}

export interface CustomFieldValue {
  id: string
  issueId: string
  fieldId: string
  value?: string | null
  numberValue?: number | null
  dateValue?: string | null
  field: CustomField
  createdAt: string
  updatedAt: string
}

export interface UpdateProjectCustomFieldsRequest {
  fields: {
    fieldId: string
    enabled: boolean
    required?: boolean
    order?: number
  }[]
}

// 獲取所有全域自訂欄位
export const getGlobalCustomFields = async (): Promise<{ customFields: CustomField[] }> => {
  const response = await fetch('/api/custom-fields', createFetchConfig())

  if (!response.ok) {
    throw new Error(`獲取自訂欄位失敗: ${response.status}`)
  }

  const result = await response.json()
  return result.data
}

// 獲取專案的自訂欄位配置
export const getProjectCustomFields = async (projectId: string): Promise<{
  enabledFields: ProjectCustomField[]
  availableFields: CustomField[]
}> => {
  const response = await fetch(getApiUrl(`projects/${projectId}/custom-fields`), createFetchConfig())

  if (!response.ok) {
    throw new Error(`獲取專案自訂欄位失敗: ${response.status}`)
  }

  const result = await response.json()
  return result.data
}

// 批量更新專案自訂欄位配置
export const updateProjectCustomFields = async (
  projectId: string,
  data: UpdateProjectCustomFieldsRequest
): Promise<{ projectCustomFields: ProjectCustomField[] }> => {
  const response = await fetch(getApiUrl(`projects/${projectId}/custom-fields`), {
    method: 'PUT',
    ...createFetchConfig(),
    body: JSON.stringify(data)
  })

  if (!response.ok) {
    throw new Error(`更新專案自訂欄位失敗: ${response.status}`)
  }

  const result = await response.json()
  return result.data
}

// 啟用專案自訂欄位
export const enableProjectCustomField = async (
  projectId: string,
  fieldId: string,
  options?: { required?: boolean; order?: number; showInCard?: boolean }
): Promise<{ projectCustomField: ProjectCustomField }> => {
  const response = await fetch(getApiUrl(`projects/${projectId}/custom-fields/${fieldId}/enable`), {
    method: 'POST',
    ...createFetchConfig(),
    body: JSON.stringify(options || {})
  })

  if (!response.ok) {
    throw new Error(`啟用專案自訂欄位失敗: ${response.status}`)
  }

  const result = await response.json()
  return result.data
}

// 停用專案自訂欄位
export const disableProjectCustomField = async (
  projectId: string,
  fieldId: string
): Promise<void> => {
  const response = await fetch(getApiUrl(`projects/${projectId}/custom-fields/${fieldId}/disable`), {
    method: 'POST',
    ...createFetchConfig()
  })

  if (!response.ok) {
    throw new Error(`停用專案自訂欄位失敗: ${response.status}`)
  }
}

// 更新專案自訂欄位卡片顯示設定
export const updateProjectCustomFieldShowInCard = async (
  projectId: string,
  fieldId: string,
  showInCard: boolean
): Promise<{ projectCustomField: ProjectCustomField }> => {
  const response = await fetch(getApiUrl(`projects/${projectId}/custom-fields/${fieldId}/show-in-card`), {
    method: 'PATCH',
    ...createFetchConfig(),
    body: JSON.stringify({ showInCard })
  })

  if (!response.ok) {
    throw new Error(`更新卡片顯示設定失敗: ${response.status}`)
  }

  const result = await response.json()
  return result.data
}

// 更新議題自訂欄位值（這個會在議題更新時調用）
export const updateIssueCustomFieldValue = async (
  issueId: string,
  fieldId: string,
  value: string | number | Date | null
): Promise<CustomFieldValue> => {
  const body: any = { fieldId }

  if (typeof value === 'string') {
    body.value = value
  } else if (typeof value === 'number') {
    body.numberValue = value
  } else if (value instanceof Date) {
    body.dateValue = value.toISOString()
  } else if (value === null) {
    body.value = null
    body.numberValue = null
    body.dateValue = null
  }

  const response = await fetch(getApiUrl(`issues/${issueId}/custom-fields`), {
    method: 'PUT',
    ...createFetchConfig(),
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    throw new Error(`更新議題自訂欄位值失敗: ${response.status}`)
  }

  const result = await response.json()
  return result.data
}
